/**
 * ProfileInfoCard Component
 *
 * User profile information cards: Display Name and Email Address.
 * Part of modularized UserAccountManagement component.
 */

// 1. External dependencies
import React from 'react';
import { User, Mail, Settings } from 'lucide-react';

// 2. Internal absolute imports
import {
  Card,
  CardBody,
  Chip,
  Button,
  useDisclosure
} from '@enfomentis/ui';
import { DisplayNameModal } from '@/components/shared/auth';
import type { User as UserType } from '@/types/auth';

// 3. Relative imports
import { EmailChangeModal } from './EmailChangeModal';

/**
 * Props for ProfileInfoCard
 */
export interface ProfileInfoCardProps {
  user: UserType;
  pendingEmailChange: { newEmail: string; createdAt: string } | null;
  onResendEmailVerification: () => void;
  onCancelEmailChange: () => void;
  onEmailChangeSuccess?: () => void;
}

/**
 * Profile Info Card Component
 *
 * Displays and manages:
 * - Display Name (with edit modal)
 * - Email Address (with change modal)
 * - Pending email changes (with resend/cancel)
 */
export const ProfileInfoCard: React.FC<ProfileInfoCardProps> = ({
  user,
  pendingEmailChange,
  onResendEmailVerification,
  onCancelEmailChange,
  onEmailChangeSuccess
}) => {
  // Display name modal state
  const { isOpen: isDisplayNameOpen, onOpen: onDisplayNameOpen, onOpenChange: onDisplayNameOpenChange } = useDisclosure();

  // Email change modal state
  const { isOpen: isEmailOpen, onOpen: onEmailOpen, onOpenChange: onEmailOpenChange } = useDisclosure();

  return (
    <>
      {/* Display Name Card */}
      <Card shadow="sm">
        <CardBody className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div className="text-primary flex-shrink-0">
                <User size={24} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-semibold text-text-primary">
                  Display Name
                </h3>
                <p className="text-text-secondary break-words">
                  {user.displayName}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="bordered"
                size="lg"
                startContent={<Settings size={16} />}
                onPress={onDisplayNameOpen}
                className="w-full sm:w-auto"
              >
                Edit
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Email Address Card */}
      <Card shadow="sm">
        <CardBody className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div className="text-primary flex-shrink-0">
                <Mail size={24} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-text-primary">
                    Email Address
                  </h3>
                  {pendingEmailChange ? (
                    <Chip
                      color="warning"
                      variant="flat"
                      size="sm"
                    >
                      Awaiting Verification
                    </Chip>
                  ) : (
                    <Chip
                      color={user.emailVerified ? "success" : "warning"}
                      variant="flat"
                      size="sm"
                    >
                      {user.emailVerified ? "Verified" : "Pending"}
                    </Chip>
                  )}
                </div>
                {pendingEmailChange ? (
                  <div className="space-y-1">
                    <p className="text-text-secondary break-all">
                      {user.email} → {pendingEmailChange.newEmail}
                    </p>
                    <p className="text-xs text-text-tertiary break-words">
                      Pending verification since {new Date(pendingEmailChange.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                ) : (
                  <p className="text-text-secondary break-all">
                    {user.email}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
              {pendingEmailChange ? (
                <>
                  <Button
                    variant="flat"
                    size="lg"
                    color="primary"
                    onPress={onResendEmailVerification}
                    className="w-full sm:w-auto"
                  >
                    Resend
                  </Button>
                  <Button
                    variant="flat"
                    size="lg"
                    color="danger"
                    onPress={onCancelEmailChange}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  variant="bordered"
                  size="lg"
                  startContent={<Settings size={16} />}
                  onPress={onEmailOpen}
                  className="w-full sm:w-auto"
                >
                  Change Email
                </Button>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Display Name Edit Modal */}
      <DisplayNameModal
        isOpen={isDisplayNameOpen}
        onOpenChange={onDisplayNameOpenChange}
        currentDisplayName={user.displayName}
      />

      {/* Email Change Modal */}
      <EmailChangeModal
        isOpen={isEmailOpen}
        onOpenChange={onEmailOpenChange}
        {...(onEmailChangeSuccess && { onSuccess: onEmailChangeSuccess })}
        {...(onEmailChangeSuccess && { onEmailChange: onEmailChangeSuccess })}
      />
    </>
  );
};
